import { useState, useEffect } from "react"
import { cn } from "@/lib/utils"

const CACHE_VERSION = "v3"
const transparentCache = new Map<string, string>()

interface TransparentTrendingIconProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string
  alt?: string
}

/**
 * TransparentTrendingIcon
 * Automatically removes off-white/solid JPEG studio backgrounds and cast floor shadows
 * in-browser using an off-screen canvas and edge flood-fill.
 *
 * This allows 3D JPEG renders to float with 100% true transparency and vibrant
 * full-luminance colors over colored backgrounds (like the Gemini ambient glow)
 * without using `mix-blend-multiply` (which darkens and dulls colors).
 */
export function TransparentTrendingIcon({
  src,
  alt = "",
  className,
  title: _title,
  ...props
}: TransparentTrendingIconProps) {
  const cacheKey = `${src}_${CACHE_VERSION}`
  const [dataUrl, setDataUrl] = useState<string>(() => transparentCache.get(cacheKey) || src)
  const [isReady, setIsReady] = useState<boolean>(() => transparentCache.has(cacheKey))

  useEffect(() => {
    if (transparentCache.has(cacheKey)) {
      setDataUrl(transparentCache.get(cacheKey)!)
      setIsReady(true)
      return
    }

    const img = new Image()
    img.crossOrigin = "anonymous"
    img.src = src

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas")
        canvas.width = img.width
        canvas.height = img.height
        const ctx = canvas.getContext("2d", { willReadFrequently: true })
        if (!ctx) return

        ctx.drawImage(img, 0, 0)
        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        const data = imgData.data
        const w = canvas.width
        const h = canvas.height

        const bgR = data[0]
        const bgG = data[1]
        const bgB = data[2]

        const visited = new Uint8Array(w * h)
        const queue: number[] = []

        function enqueue(x: number, y: number) {
          const idx = y * w + x
          if (!visited[idx]) {
            visited[idx] = 1
            queue.push(idx)
          }
        }

        // Seed BFS with all 4 outer border edges
        for (let x = 0; x < w; x++) {
          enqueue(x, 0)
          enqueue(x, h - 1)
        }
        for (let y = 0; y < h; y++) {
          enqueue(0, y)
          enqueue(w - 1, y)
        }

        // BFS flood-fill only outer background and neutral floor cast shadow pixels
        let head = 0
        while (head < queue.length) {
          const idx = queue[head++]
          const pi = idx * 4
          const r = data[pi]
          const g = data[pi + 1]
          const b = data[pi + 2]

          const diff = Math.max(Math.abs(r - bgR), Math.abs(g - bgG), Math.abs(b - bgB))
          const minChannel = Math.min(r, g, b)
          const maxChannel = Math.max(r, g, b)
          const saturation = maxChannel - minChannel

          // Studio floor shadow is neutral/achromatic (saturation <= 30) on the floor (minChannel >= 115)
          const isFloorShadow = saturation <= 30 && minChannel >= 115
          // Background condition: matches corner reference, near-white backdrop, or neutral cast shadow
          const isBackground = diff <= 22 || minChannel >= 232 || isFloorShadow

          if (isBackground) {
            data[pi + 3] = 0 // Fully transparent

            const x = idx % w
            const y = Math.floor(idx / w)

            if (x > 0) enqueue(x - 1, y)
            if (x < w - 1) enqueue(x + 1, y)
            if (y > 0) enqueue(x, y - 1)
            if (y < h - 1) enqueue(x, y + 1)
          } else if (diff <= 36 || minChannel >= 212 || (saturation <= 38 && minChannel >= 100)) {
            // Clean anti-aliased edge falloff
            data[pi + 3] = 0
          }
        }

        ctx.putImageData(imgData, 0, 0)
        const outUrl = canvas.toDataURL("image/png")
        transparentCache.set(cacheKey, outUrl)
        setDataUrl(outUrl)
        setIsReady(true)
      } catch (err) {
        console.warn("Could not make icon transparent:", err)
        setIsReady(true)
      }
    }
  }, [src, cacheKey])

  return (
    <img
      src={dataUrl}
      alt={alt}
      className={cn(
        "transition-opacity duration-150",
        isReady ? "opacity-100" : "opacity-0",
        className
      )}
      loading="eager"
      decoding="async"
      {...props}
    />
  )
}
